import { useState } from 'react';

import { Button, CheckBox, ErrorMessage, Label } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { useCommissionConsent } from './useCommissionConsent';
import { MoneyWithDecimals } from '../../../../../ui/moneyWithDecimals';
import { VariableRate } from '../../../../../ui/rate';
import { InnerLayout } from '../../InnerLayout/InnerLayout';

export const CommissionConsentForm = () => {
    const { commissionDetails, submitConsent, isSubmitting } =
        useCommissionConsent();

    const isAlreadyConsented = Boolean(commissionDetails.consent_given_at);
    const [isChecked, setIsChecked] = useState(isAlreadyConsented);
    const [showError, setShowError] = useState(false);

    const handleConfirm = () => {
        if (!isChecked) {
            setShowError(true);
            return;
        }
        submitConsent();
    };

    return (
        <InnerLayout
            requirementType="COMMISSION_CONSENT"
            title={
                <FormattedMessage
                    id="_.funding_requirements.commission_consent_step.title"
                    defaultMessage="Acknowledge Introducer Commission"
                />
            }
            subheading={
                <CommissionMessage commissionDetails={commissionDetails} />
            }
        >
            <div className="my-2xl">
                <div className="flex items-start gap-s">
                    <CheckBox
                        id="commission-consent-checkbox"
                        checked={isChecked}
                        disabled={isAlreadyConsented}
                        onChange={(event) => {
                            setIsChecked(event.currentTarget.checked);
                            if (event.currentTarget.checked) {
                                setShowError(false);
                            }
                        }}
                    />
                    <Label
                        className="cursor-pointer pt-xs font-reg"
                        htmlFor="commission-consent-checkbox"
                    >
                        <FormattedMessage
                            id="_.funding_requirements.commission_consent.checkbox_label"
                            defaultMessage="I acknowledge that iwoca will pay this commission"
                        />
                    </Label>
                </div>
                {showError && (
                    <div className="mt-s">
                        <ErrorMessage>
                            <FormattedMessage
                                id="_.funding_requirements.commission_consent.checkbox_error"
                                defaultMessage="Required"
                            />
                        </ErrorMessage>
                    </div>
                )}
            </div>

            <Button
                variant="primary-rounded"
                onClick={handleConfirm}
                loading={isSubmitting}
                disabled={isAlreadyConsented}
            >
                <FormattedMessage
                    id="_.funding_requirements.commission_consent.confirm_button"
                    defaultMessage="Confirm and return to list"
                />
            </Button>
        </InnerLayout>
    );
};

const CommissionMessage = ({
    commissionDetails
}: {
    commissionDetails: {
        commission_amount: number;
        commission_rate: number;
        extra_commission_rate: number;
        cash_amount: number;
        introducer_name: string;
        commission_type: string;
    };
}) => {
    return (
        <>
            {commissionDetails.commission_amount >= 0.001 && (
                <FormattedMessage
                    id="_.funding_requirements.commission_consent.subtitle"
                    defaultMessage="iwoca will pay {commission_amount} ({commission_rate} of {cash_amount}) to {introducer_name} as commission for this funding."
                    values={{
                        commission_amount: (
                            <MoneyWithDecimals
                                value={commissionDetails.commission_amount}
                            />
                        ),
                        commission_rate: (
                            <VariableRate
                                minimumFractionDigits={1}
                                maximumFractionDigits={2}
                                value={commissionDetails.commission_rate}
                            />
                        ),
                        cash_amount: (
                            <MoneyWithDecimals
                                value={commissionDetails.cash_amount}
                            />
                        ),
                        introducer_name: commissionDetails.introducer_name
                    }}
                />
            )}
            {commissionDetails.commission_type === 'VOLUME_INCENTIVE' && (
                <p className="mt-l" data-testid="volume-incentive-message">
                    <FormattedMessage
                        id="_.funding_requirements.commission_consent.volume_incentive"
                        defaultMessage="iwoca may also pay {introducer_name} an additional commission based on the total value of loans they introduce to us over time. This additional commission generally makes up no more than {extra_commission_percentage} of the total drawn amount. We set additional commissions independently of setting the interest or fees on your loan with us."
                        values={{
                            extra_commission_percentage: (
                                <VariableRate
                                    minimumFractionDigits={1}
                                    maximumFractionDigits={2}
                                    value={
                                        commissionDetails.extra_commission_rate
                                    }
                                />
                            ),
                            introducer_name: commissionDetails.introducer_name
                        }}
                    />
                </p>
            )}
            {commissionDetails.commission_type === 'REVENUE_SHARE' && (
                <p className="mt-l" data-testid="revenue-share-message">
                    <FormattedMessage
                        id="_.funding_requirements.commission_consent.revenue_share"
                        defaultMessage="When you make repayments to iwoca on this loan, we'll share up to {extra_commission_percentage} of the interest and/or fees (not loan principal) with {introducer_name} as a commission. The exact percentage will be determined by the total value of loans {introducer_name} introduces to us over time. We set commissions independently of setting the interest or fees on your loan with us."
                        values={{
                            extra_commission_percentage: (
                                <VariableRate
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                    value={
                                        commissionDetails.extra_commission_rate
                                    }
                                />
                            ),
                            introducer_name: commissionDetails.introducer_name
                        }}
                    />
                </p>
            )}
        </>
    );
};
